import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CreditCard, DollarSign, CheckCircle, Clock, 
  AlertCircle, ArrowRight, Receipt, Wallet, Coins, Zap
} from 'lucide-react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { api } from '../lib/auth';
import { formatCurrency, formatDateTime, cn } from '../lib/utils';
import { toast } from 'sonner';

export default function BillingPage() {
  const [searchParams] = useSearchParams();
  const [transactions, setTransactions] = useState([]);
  const [organization, setOrganization] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [pendingSessions, setPendingSessions] = useState([]);
  const [credits, setCredits] = useState({ publishing_credits: 0, credit_cost_per_publish: 25, has_fomo_bonus: false });
  const [loading, setLoading] = useState(true);
  const [checkingPayment, setCheckingPayment] = useState(false);

  useEffect(() => {
    loadData();
    
    // Check if returning from Stripe
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, []);

  const loadData = async () => {
    try {
      const [txRes, orgRes, proposalsRes, creditsRes, sessionsRes] = await Promise.all([
        api.getTransactions(),
        api.getMyOrganization(),
        api.getProposals(),
        api.getMyCredits(),
        api.getZoomRequests().catch(() => ({ data: [] }))  // DWY sessions
      ]);
      
      // Include paid DWY sessions as transactions for display
      const dwyTransactions = (sessionsRes.data || [])
        .filter(s => s.payment_status === 'paid' && s.price > 0)
        .map(s => ({
          id: s.id,
          amount: s.price,
          status: 'paid',
          description: `DWY Session: ${s.topic}`,
          created_at: s.created_at,
          isDWYSession: true
        }));
      
      // Combine transactions
      const allTransactions = [...txRes.data, ...dwyTransactions].sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      
      setTransactions(allTransactions);
      setOrganization(orgRes.data);
      setProposals(proposalsRes.data);
      setCredits(creditsRes.data);
      
      // Store pending DWY sessions for display
      const pendingDWY = (sessionsRes.data || [])
        .filter(s => s.payment_status !== 'paid' && s.status !== 'cancelled' && s.price > 0);
      setPendingSessions(pendingDWY);
    } catch (error) {
      console.error('Failed to load billing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) {
      toast.info('Payment processing. Please refresh to check status.');
      return;
    }

    setCheckingPayment(true);
    try {
      const response = await api.getPaymentStatus(sessionId);
      
      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful! Thank you.');
        loadData();
        setCheckingPayment(false);
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired');
        setCheckingPayment(false);
      } else {
        // Poll again
        setTimeout(() => checkPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      setCheckingPayment(false);
    }
  };

  const handlePayProposal = async (proposalId) => {
    try {
      const response = await api.createCheckout(proposalId);
      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Failed to initiate payment');
    }
  };

  const pendingProposals = proposals.filter(p => p.status === 'sent' && p.due_amount > 0);
  const totalPendingProposals = pendingProposals.reduce((sum, p) => sum + p.due_amount, 0);
  const totalPendingSessions = pendingSessions.reduce((sum, s) => sum + (s.price || 0), 0);
  const totalPending = totalPendingProposals + totalPendingSessions;
  const totalPaid = transactions
    .filter(t => t.status === 'paid')
    .reduce((sum, t) => sum + t.amount, 0);

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="font-heading text-3xl font-bold text-white mb-2">Billing</h1>
          <p className="text-gray-400">Manage payments and view transaction history</p>
        </motion.div>

        {/* Summary Cards */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8"
        >
          {/* Publishing Credits Card */}
          <div className="glass rounded-xl p-6 border border-primary/30">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                <Coins className="w-5 h-5 text-primary" />
              </div>
              <span className="text-gray-400">Publishing Credits</span>
            </div>
            <p className="text-2xl font-heading font-bold text-white">
              {credits.publishing_credits?.toLocaleString() || 0}
            </p>
            <p className="text-sm text-gray-500 mt-1">{credits.credit_cost_per_publish} credits per post</p>
            <Link to="/pricing">
              <Button variant="outline" size="sm" className="mt-3 w-full text-primary border-primary/30 hover:bg-primary/10">
                <Zap className="w-4 h-4 mr-2" />
                Upgrade to get more credits
              </Button>
            </Link>
          </div>

          <div className="glass rounded-xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <Wallet className="w-5 h-5 text-purple-400" />
              </div>
              <span className="text-gray-400">Co-Op Balance</span>
            </div>
            <p className="text-2xl font-heading font-bold text-white">
              {formatCurrency(organization?.coop_balance || 0)}
            </p>
            <p className="text-sm text-gray-500 mt-1">Available coverage</p>
          </div>

          <div className="glass rounded-xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                <Clock className="w-5 h-5 text-yellow-400" />
              </div>
              <span className="text-gray-400">Pending</span>
            </div>
            <p className="text-2xl font-heading font-bold text-white">
              {formatCurrency(totalPending)}
            </p>
            <p className="text-sm text-gray-500 mt-1">{pendingProposals.length + pendingSessions.length} item(s)</p>
          </div>

          <div className="glass rounded-xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
              <span className="text-gray-400">Total Paid</span>
            </div>
            <p className="text-2xl font-heading font-bold text-white">
              {formatCurrency(totalPaid)}
            </p>
            <p className="text-sm text-gray-500 mt-1">Lifetime</p>
          </div>
        </motion.div>

        {/* Payment Checking Indicator */}
        {checkingPayment && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="glass rounded-xl p-4 mb-8 flex items-center gap-3"
          >
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <span className="text-white">Verifying payment status...</span>
          </motion.div>
        )}

        {/* Pending Proposals */}
        {pendingProposals.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="mb-8"
          >
            <h2 className="font-heading text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-yellow-400" />
              Pending Payments
            </h2>
            <div className="space-y-4">
              {pendingProposals.map((proposal) => (
                <div 
                  key={proposal.id}
                  className="glass rounded-xl p-6 border-2 border-yellow-500/30"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <p className="text-sm text-gray-400 mb-1">Proposal for Order</p>
                      <p className="font-medium text-white">{proposal.order_id.slice(0, 8)}...</p>
                      <div className="flex items-center gap-4 mt-2 text-sm">
                        <span className="text-gray-400">
                          Total: {formatCurrency(proposal.total)}
                        </span>
                        {proposal.covered_amount > 0 && (
                          <span className="text-green-400">
                            -{formatCurrency(proposal.covered_amount)} covered
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-heading font-bold text-white mb-2">
                        {formatCurrency(proposal.due_amount)}
                      </p>
                      <Button 
                        onClick={() => handlePayProposal(proposal.id)}
                        className="btn-primary"
                        data-testid={`pay-proposal-${proposal.id}`}
                      >
                        Pay Now <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Transaction History */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <h2 className="font-heading text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <Receipt className="w-5 h-5" />
            Transaction History
          </h2>

          <div className="glass rounded-xl overflow-hidden">
            {loading ? (
              <div className="p-8 text-center">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
              </div>
            ) : transactions.length === 0 ? (
              <div className="p-8 text-center">
                <CreditCard className="w-10 h-10 text-gray-600 mx-auto mb-2" />
                <p className="text-gray-400">No transactions yet</p>
              </div>
            ) : (
              <div className="divide-y divide-white/10">
                {transactions.map((tx) => (
                  <div 
                    key={tx.id}
                    className="p-4 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-4">
                      <div className={cn(
                        "w-10 h-10 rounded-lg flex items-center justify-center",
                        tx.status === 'paid' 
                          ? "bg-green-500/20" 
                          : tx.status === 'pending'
                          ? "bg-yellow-500/20"
                          : "bg-gray-500/20"
                      )}>
                        {tx.status === 'paid' ? (
                          <CheckCircle className="w-5 h-5 text-green-400" />
                        ) : tx.status === 'pending' ? (
                          <Clock className="w-5 h-5 text-yellow-400" />
                        ) : (
                          <DollarSign className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-white">
                          {formatCurrency(tx.amount)}
                        </p>
                        <p className="text-sm text-gray-500">
                          {formatDateTime(tx.created_at)}
                        </p>
                      </div>
                    </div>
                    <span className={cn(
                      "px-3 py-1 rounded-full text-xs font-medium",
                      tx.status === 'paid' 
                        ? "bg-green-500/20 text-green-400"
                        : tx.status === 'pending'
                        ? "bg-yellow-500/20 text-yellow-400"
                        : "bg-gray-500/20 text-gray-400"
                    )}>
                      {tx.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </motion.div>

        {/* Support */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mt-8 p-6 rounded-xl bg-white/5 border border-white/10 text-center"
        >
          <p className="text-gray-400 mb-2">Questions about billing?</p>
          <p className="text-white">
            Contact us at{' '}
            <a href="mailto:support@aihorsepowered.com" className="text-primary hover:underline">
              support@aihorsepowered.com
            </a>
            {' '}or call{' '}
            <a href="tel:8337727397" className="text-primary hover:underline">
              833-772-7397
            </a>
          </p>
        </motion.div>
      </div>
    </DashboardLayout>
  );
}
